import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../../../AgGridStyle.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Box, Typography, CircularProgress, Tooltip, IconButton, Button } from '@mui/material';
import { usePermissions } from '../../../GlobalFunction/userPermissions';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../../redux/propertySlice';
import { getRoomMaster } from '../../../Api/RoomMst/Api';
import { useNavigate, useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import HomeIcon from '@mui/icons-material/Home';
import RoomMasterFormOneButton from './RoomMasterFormOneButton';
import { getFormMode } from "../../../lib/helpers";
import { useTrackVisit } from '../../../hooks/useTrackVisit';

ModuleRegistry.registerModules([AllCommunityModule]);

const RoomMasterTable = () => {
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const gridRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = sessionStorage.getItem('UserId');
  const permissions = usePermissions("Room Master");
  const { AddRec = "0" } = permissions || {};
  const globalProperty = useSelector(selectGlobalProperty);
  const { navigateAndTrack } = useTrackVisit("Room Master Table");

  const Form_Mode = getFormMode();
  const [mode] = useState(() =>
    location?.state?.RoomId ? Form_Mode.read : Form_Mode.add
  );

  const colDefs = useMemo(() => [
    { field: 'srNo', headerName: 'Sr.No', width: 70 },
    { field: 'PropName', headerName: 'Property', width: 200 },
    { field: 'WingName', headerName: 'Wing', width: 100 },
    { field: 'FloorName', headerName: 'Floor', width: 100 },
    { field: 'RoomTypeName', headerName: 'Type', width: 150 },
    { field: 'RoomViewName', headerName: 'View', width: 120 },
    { field: 'RoomNo', headerName: 'Room No', width: 120 },
    { field: 'SqFt', headerName: 'SqFt', width: 100 },
    { field: 'TotalBeds', headerName: 'Total Beds', width: 130 },
    { field: 'MonthlyRent', headerName: 'Rent', width: 120 },
    { field: 'Remark', headerName: 'Remark', width: 100 },
    {
      field: 'Status',
      headerName: 'Status',
      width: 120,
      cellStyle: (params) => ({
        color: params.value === 'Active' ? 'green' : 'red',
        fontWeight: 'bold',
      }),
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    tooltipValueGetter: (params) => params.value ?? '',
  }), []);

  const fetchRoomData = useCallback(async () => {
    if (!globalProperty) {
      setRowData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await getRoomMaster({
        start: 1,
        PageSize: 1,
        SearchText: globalProperty,
      });

      if (response?.data?.Status === 0 && response.data.Data) {
        const formattedData = response.data.Data.map((room, index) => ({
          ...room,
          srNo: index + 1,
          Status: room.Status == 1 ? 'Active' : 'Inactive',
          RoomViewName: room.RoomViewName || '',
        }));
        setRowData(formattedData);
      } else {
        setRowData([]);
        toast.info(response?.data?.message || 'No records found');
      }
    } catch (error) {
      console.error('Error fetching room data:', error);
      toast.error('Failed to load room data');
      setRowData([]);
    } finally {
      setIsLoading(false);
    }
  }, [globalProperty]);

  useEffect(() => {
    fetchRoomData();
  }, [fetchRoomData]);

  const handleRowDoubleClick = useCallback((params) => {
    const roomId = params?.data?.RoomId;
    if (roomId) {
      navigateAndTrack('/room-master', "Room Master Form");
      navigate('/room-master', { state: { RoomId: roomId, mode: 'view' } });
    }
  }, [navigate]);

  const handlePermittedClick = () => {
    if (AddRec === "1") {
      navigateAndTrack('/room-master', "Room Master Form");
      navigate('/room-master');
    }
  };

  const handleClearFilters = () => {
    gridRef.current?.api?.setFilterModel(null);
  };

  const refreshData = () => {
    fetchRoomData();
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '90vh',
        padding: '10px',
        backgroundColor: 'rgb(249, 249, 249)',
        position: 'relative'
      }}
    >
      <ToastContainer />

      <Box
        sx={{
          backgroundColor: "#ffffff",
          padding: { xs: "8px 16px", sm: "12px 16px" },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          borderRadius: "12px",
          margin: "10px auto",
          maxWidth: "95%",
          flexDirection: { xs: "column", sm: "column", md: "row" },
          gap: 2,
        }}
      >
        <Breadcrumbs aria-label="breadcrumb" separator="›">
          <Link
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
            onClick={() => navigate('/dashboard')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer", fontWeight: "500" }}
            onClick={() => navigate('/masters/property')}
          >
            Property
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: "500" }}>
            Room Master
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: { xs: 1, sm: 1, md: 0 }, flexShrink: 0 }}>
          {/* Clear Filters */}
          <Tooltip title='Clear all applied filter' arrow>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              size="small"
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                borderColor: "#635bff",
                color: "#635bff",
                "&:hover": {
                  borderColor: "#524ccc",
                  backgroundColor: "rgba(99, 91, 255, 0.04)",
                }
              }}
            >
              Clear Filters
            </Button>
          </Tooltip>

          <Tooltip title='Bed Configure'>
            <RoomMasterFormOneButton
              PropId={globalProperty}
              formMode={mode}
              UserId={userId}
              refreshRoomData={refreshData}
            />
          </Tooltip>

          {/* Add New Button */}
          <Tooltip
            title={AddRec !== "1" ? "You have no permission to add record" : "Add New Record"}
            placement="top"
            arrow
          >
            <span>
              <Button
                variant="contained"
                onClick={handlePermittedClick}
                size="small"
                disabled={AddRec !== "1"}
                sx={{
                  background: AddRec === "1" ? "#635bff" : "rgba(0, 0, 0, 0.12)",
                  color: AddRec === "1" ? "white" : "rgba(0, 0, 0, 0.26)",
                  borderRadius: "50px",
                  padding: "6px 15px",
                  boxShadow: AddRec === "1" ? "0px 6px 15px rgba(0, 0, 0, 0.2)" : "none",
                  "&:hover": {
                    background: AddRec === "1" ? "#524ccc" : "rgba(0, 0, 0, 0.12)"
                  }
                }}
              >
                <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> New
              </Button>
            </span>
          </Tooltip>

          {/* Back Button */}
          <Tooltip title='Previous Page' arrow>
            <IconButton
              onClick={() => navigate('/masters/property')}
              sx={{
                backgroundColor: '#635bff',
                borderRadius: '50%',
                width: 35,
                height: 35,
                '&:hover': { backgroundColor: '#635bff' },
                boxShadow: 3,
              }}
            >
              <ArrowBackIcon sx={{ color: 'white' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box className="table-grid ag-theme-alpine"
        sx={{ height: 'calc(97% - 56px)', width: '95%', margin: '0 auto', position: 'relative' }}
      >
        {isLoading ? (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              textAlign: 'center',
            }}
          >
            <CircularProgress size="3rem" sx={{ color: '#635bff' }} />
            <Typography sx={{ marginTop: 2, color: '#635bff', fontWeight: 'bold' }}>
              Loading data...
            </Typography>
          </Box>
        ) : (
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={1000}
            onRowDoubleClicked={handleRowDoubleClick}
            paginationPageSizeSelector={[100, 500, 1000, 5000]}
            enableCellTextSelection={true}
            rowHeight={25}
            headerHeight={30}
            floatingFiltersHeight={28}
            overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No rooms to show</span>'}
            tooltipShowDelay={0}
            getRowStyle={(params) => ({
              backgroundColor: params.node.rowIndex % 2 === 0 ? '#f1f5fd' : '#ffffff'
            })}
          />
        )}
      </Box>

      <Box sx={{
        position: 'absolute',
        bottom: 16,
        left: 70,
        fontSize: '14px',
        color: 'black',
      }}>
        Total Records: <b>{rowData.length}</b>
      </Box>
    </Box>
  );
};

export default RoomMasterTable;